import { ApiQuest, ApiQuestDetail, ApiUpdateQuestPayload } from "griptonite-api-client/types";
import { z } from "zod";

import { BonusFormSchema, bonusFormValuesToPayload, bonusToFormValues } from "./bonusForms";
import { boostFormValuesToPayload, boostToFormValues, makeBoostFormSchema } from "./boostForms";

const QuestPointsThresholdSchema = z.object({
  uuid: z.string().optional(),
  points: z
    .number({ message: "Points goal required" })
    .int({ message: "Must be a whole number" })
    .min(1, { message: "Must be at least 1" }),
  creditReward: z
    .number({ message: "Credits required" })
    .int({ message: "Must be a whole number" })
    .min(1, { message: "Must be at least 1" }),
});

const PointsThresholdsSchema = z
  .array(QuestPointsThresholdSchema)
  .min(1, { message: "At least one milestone required" })
  .superRefine((thresholds, ctx) => {
    const seen = new Set<number>();
    thresholds.forEach((threshold, index) => {
      if (seen.has(threshold.points)) {
        ctx.addIssue({
          message: "Two milestones can't have the same points goal",
          code: z.ZodIssueCode.custom,
          path: [index, "points"],
        });
      }
      seen.add(threshold.points);
    });
  });

// Only leaderboard points quests have milestones; other types leave the list empty.
export const makeQuestRulesFormSchema = (quest: Pick<ApiQuest, "type" | "startTime" | "endTime">) =>
  z.object({
    pointsThresholds:
      quest.type === "LEADERBOARD_POINTS"
        ? PointsThresholdsSchema
        : z.array(QuestPointsThresholdSchema),
    bonuses: z.array(BonusFormSchema),
    boosts: z.array(makeBoostFormSchema(quest)),
  });

export type QuestRulesFormValues = z.infer<ReturnType<typeof makeQuestRulesFormSchema>>;

export const sortThresholds = <T extends { points: number | null }>(thresholds: T[]) =>
  [...thresholds].sort((a, b) => (a.points ?? Infinity) - (b.points ?? Infinity));

export const questToRulesFormValues = (quest: ApiQuestDetail): QuestRulesFormValues => ({
  pointsThresholds: sortThresholds(
    (quest.pointsThresholds ?? []).map(({ uuid, points, creditReward }) => ({
      uuid,
      points,
      creditReward,
    })),
  ),
  bonuses: quest.bonuses.map((bonus) => bonusToFormValues(bonus, [])),
  boosts: quest.boosts.map((boost) => boostToFormValues(boost)),
});

// The API replaces each list whole, so all three are always sent in full. Items keep their uuid,
// and new ones go without. An unchanged ladder isn't treated as an edit, even on a quest whose
// ladder is frozen.
export const questRulesFormValuesToPayload = (
  values: QuestRulesFormValues,
  quest: Pick<ApiQuest, "type">,
): ApiUpdateQuestPayload => ({
  ...(quest.type === "LEADERBOARD_POINTS"
    ? {
        pointsThresholds: sortThresholds(values.pointsThresholds).map(
          ({ uuid, points, creditReward }) => ({ uuid, points, creditReward }),
        ),
      }
    : {}),
  bonuses: values.bonuses.map((bonus) => ({
    uuid: bonus.uuid,
    ...bonusFormValuesToPayload(bonus),
  })),
  boosts: values.boosts.map((boost) => ({
    uuid: boost.uuid,
    ...boostFormValuesToPayload(boost),
  })),
});

// The estimate is asked for as the tab is edited, and an item still being filled in would fail the
// API's checks for the whole request, so only complete items are counted.
export const questRulesFormValuesToEstimateLists = (
  values: QuestRulesFormValues,
  quest: Pick<ApiQuest, "type" | "startTime" | "endTime">,
) => {
  const complete = <T>(schema: z.ZodType<T>, items: unknown[]) =>
    items.flatMap((item) => {
      const result = schema.safeParse(item);
      return result.success ? [result.data] : [];
    });
  return questRulesFormValuesToPayload(
    {
      pointsThresholds: complete(QuestPointsThresholdSchema, values.pointsThresholds),
      bonuses: complete(BonusFormSchema, values.bonuses),
      boosts: complete(makeBoostFormSchema(quest), values.boosts),
    },
    quest,
  );
};
